import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    DOCUMENT,
    Inject,
    OnInit
} from '@angular/core';
import {nanoid} from 'nanoid';
import {EMPTY, switchMap} from 'rxjs';
import {distinctUntilChanged, filter, takeUntil} from 'rxjs/operators';
import {getUrlRelativeToPublicPath} from '../../common/public-path';
import {AppConfigService} from '../../services/config.service';
import {destroy$} from '../destroyable';
import {ModalService} from '../modal';
import {ALERT_COMMON_STRINGS, AlertModalComponent} from '../modal/alert-modal';
import {DynRemote} from './dyn-xyz-wc/dyn-remote';
import {DYN_WC_FILENAME} from './dyn-xyz-wc/dyn-wc-filename';
import {DynamicZyxDaemonService} from './dynamic-zyx-daemon.service';

@Component({
    selector:        'dynamic-xyz-daemon',
    templateUrl:     './dynamic-xyz-daemon.component.html',
    styleUrls:       ['./dynamic-xyz-daemon.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas:         [
        CUSTOM_ELEMENTS_SCHEMA
    ]
})
export class DynamicXyzDaemonComponent implements OnInit {

    private static scriptTagId = `dyn-xyz-wc_${nanoid()}`;

    public readonly destroy$ = destroy$();

    public dynRemote: DynRemote;

    public readonly dynConfig = this.appConfigService.appConfig.dynamicXyz;

    /**
     * Render the web-component only when
     * the JS with its declaration is loaded.
     * Otherwise, the bindings of props and attrs don't work.
     */
    public isWebComponentLoaded: boolean = false;

    constructor(
        private dynamicZyxDaemonService: DynamicZyxDaemonService,
        private appConfigService: AppConfigService,
        private modalService: ModalService,
        private changeDetectorRef: ChangeDetectorRef,

        @Inject(DOCUMENT)
        private document: Document
    ) {}

    public ngOnInit() {
        // this.loadDynXyzWebComponent(); // TODO
        this.loadWcAsScriptTag();
    }

    public handleDynCallback(event: CustomEvent<unknown>): void {
        console.warn('dynCallback', event); // TODO
    }

    public handleDynRemote(event: CustomEvent<DynRemote>): void {
        console.warn('dynRemote', event); // TODO

        this.dynRemote = event.detail;

        this.dynamicZyxDaemonService.setDynRemote(this.dynRemote);

        if (this.dynRemote) {
            this.listenToSelectedNetwork();
        }
    }

    /**
     * Allows extracting the web-components js-script as a separate file.
     */
    // private loadDynXyzWebComponent(): void {
    //     fromPromise(import('./dyn-xyz-wc/build/index.js' as any)).pipe(
    //         tap(() => {
    //             this.isWebComponentLoaded = true;
    //             this.changeDetectorRef.markForCheck();
    //         }),
    //         takeUntil(this.destroy$)
    //     ).subscribe();
    // }

    private async loadWcAsScriptTag() {
        if (this.document.getElementById(DynamicXyzDaemonComponent.scriptTagId)) {
            return undefined;
        }

        return new Promise((resolve, reject) => {
            const script = this.document.createElement('script');
            script.src = getUrlRelativeToPublicPath(`assets/dyn-xyz/${DYN_WC_FILENAME}.js`);
            script.async = true;

            script.onload = () => {
                this.isWebComponentLoaded = true;
                this.changeDetectorRef.markForCheck();

                console.warn('Dyn WC script loaded'); // TODO
            };

            script.onerror = (error) => {
                reject(new Error('Dyn WC script loading error: ' + JSON.stringify(error)));
            };

            this.document.head.appendChild(script);
        });
    }

    private listenToSelectedNetwork(): void {
        const isNetworkSupported = (network: number | string) =>
            Number(network) === Number(this.appConfigService.appConfig.dex.chain.id);

        this.dynRemote.network$.pipe(
            filter(selectedNetwork => !!selectedNetwork),
            distinctUntilChanged((a, b) => isNetworkSupported(a) === isNetworkSupported(b)),
            switchMap(network => {
                return isNetworkSupported(network)
                    ? EMPTY
                    : this.modalService.open(AlertModalComponent, {
                        destroyWhen: this.destroy$,
                        configure:   {
                            alertTitle:            'Not Supported Network',
                            alertTitleTranslate:   false,
                            alertButtonText:       ALERT_COMMON_STRINGS.buttonConfirm,
                            alertMessage:          'The selected network is not supported',
                            alertMessageTranslate: false
                        }
                    }).asObservable();
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

}
